import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PermissionContext = createContext({
  permissions: [],
  isAdmin: false,
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  refresh: () => {}
});

function seedFromUser(user) {
  const session = window.__APP_SESSION__;
  const perms =
    (Array.isArray(user?.permissions) && user.permissions.length > 0 && user.permissions) ||
    (Array.isArray(session?.user?.permissions) && session.user.permissions) ||
    (Array.isArray(session?.permissions) && session.permissions) ||
    [];
  const admin =
    perms.includes('*') ||
    user?.role === 'company_admin' ||
    user?.role === 'superadmin' ||
    session?.user?.role === 'company_admin' ||
    session?.user?.role === 'superadmin';
  return { perms, admin, seeded: perms.length > 0 || admin };
}

export const PermissionProvider = ({ children, user }) => {
  const initial = seedFromUser(user);
  const [permissions, setPermissions] = useState(initial.perms);
  const [isAdmin, setIsAdmin] = useState(initial.admin);
  const [loading, setLoading] = useState(!initial.seeded);

  const fetchPermissions = useCallback(async ({ force = false } = {}) => {
    if (!user) {
      setPermissions([]);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    const seeded = seedFromUser(user);
    if (seeded.seeded && !force) {
      if (seeded.perms.length) setPermissions(seeded.perms);
      setIsAdmin(seeded.admin);
      setLoading(false);
      return;
    }

    if (Array.isArray(user.permissions) && user.permissions.length > 0) {
      setPermissions(user.permissions);
      setIsAdmin(
        user.permissions.includes('*') ||
          user.role === 'company_admin' ||
          user.role === 'superadmin'
      );
    }

    try {
      if (!seeded.seeded) setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/rbac/permissions/my`);
      const apiPermissions = response.data.permissions || [];
      setPermissions((prev) => {
        if (apiPermissions.length > 0) return apiPermissions;
        if (prev.length > 0) return prev;
        if (Array.isArray(user.permissions) && user.permissions.length > 0) return user.permissions;
        return [];
      });
      setIsAdmin((prev) => {
        if (response.data.is_admin) return true;
        if (prev) return prev;
        if (
          user.permissions?.includes('*') ||
          user.role === 'company_admin' ||
          user.role === 'superadmin'
        )
          return true;
        return false;
      });
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions((prev) => {
        if (prev.length > 0) return prev;
        if (Array.isArray(user.permissions) && user.permissions.length > 0) return user.permissions;
        return [];
      });
      setIsAdmin((prev) => {
        if (prev) return prev;
        if (
          user.permissions?.includes('*') ||
          user.role === 'company_admin' ||
          user.role === 'superadmin'
        )
          return true;
        return false;
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions({ force: false });
  }, [user?.id, user?.company_id, fetchPermissions]);

  useEffect(() => {
    const handleRoleChange = () => fetchPermissions({ force: true });
    window.addEventListener('rbac-role-changed', handleRoleChange);
    return () => window.removeEventListener('rbac-role-changed', handleRoleChange);
  }, [fetchPermissions]);

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(() => fetchPermissions({ force: true }), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id, user?.company_id, fetchPermissions]);

  const hasPermission = useCallback(
    (permissionKey) => {
      if (isAdmin) return true;
      return permissions.includes(permissionKey);
    },
    [isAdmin, permissions]
  );

  const hasAnyPermission = useCallback(
    (...permissionKeys) => {
      if (isAdmin) return true;
      return permissionKeys.some((key) => permissions.includes(key));
    },
    [isAdmin, permissions]
  );

  const hasAllPermissions = useCallback(
    (...permissionKeys) => {
      if (isAdmin) return true;
      return permissionKeys.every((key) => permissions.includes(key));
    },
    [isAdmin, permissions]
  );

  const value = useMemo(
    () => ({
      permissions,
      isAdmin,
      loading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      refresh: () => fetchPermissions({ force: true }),
    }),
    [permissions, isAdmin, loading, hasPermission, hasAnyPermission, hasAllPermissions, fetchPermissions]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export const WithPermission = ({ permission, permissions, requireAll = false, fallback = null, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = usePermissions();

  if (isAdmin) return children;

  let allowed = false;
  if (permission) {
    allowed = hasPermission(permission);
  } else if (permissions) {
    allowed = requireAll ? hasAllPermissions(...permissions) : hasAnyPermission(...permissions);
  }

  return allowed ? children : fallback;
};

export const PermissionGate = ({ permission, permissions, requireAll, fallback, children }) => (
  <WithPermission
    permission={permission}
    permissions={permissions}
    requireAll={requireAll}
    fallback={fallback}
  >
    {children}
  </WithPermission>
);

export default PermissionContext;
